import type { NarrowPassageDirection } from "./api";
import { formatNumber } from "./localization";

export type AnimationSide = { side: 1 | 2; name: string; direction: NarrowPassageDirection; count: number; mean: number | null; max: number | null };

// Static schematic without OSM: side 1 left, side 2 right, the narrow section in between.
// Each side waits between its outer and inner line, so the circle sits there.
const width = 720;
const height = 250;
const road = { top: 120, bottom: 160 };
const narrow = { left: 330, right: 390, top: 130, bottom: 150 };
const maximumRadius = 70;
const layout = {
  1: { cx: 220, outer: 130, inner: 310, textX: 16, anchor: "start", toward: 2 },
  2: { cx: 500, outer: 590, inner: 410, textX: width - 16, anchor: "end", toward: 1 },
} as const;

function seconds(value: number) {
  return `${formatNumber(value, { maximumFractionDigits: 1 })} s`;
}

function animationSideLabel(side: AnimationSide) {
  return side.mean === null || side.max === null
    ? `Seite ${side.side}: keine Wartezeit erfasst`
    : `Seite ${side.side}: Mittel ${seconds(side.mean)}, Maximum ${seconds(side.max)}, ${formatNumber(side.count)} ${side.count === 1 ? "Durchfahrt" : "Durchfahrten"} mit Wartezeit`;
}

// The circle area is proportional to the mean wait; scaleMean fills the dashed reference circle.
// Narrow screens hide the SVG texts and show the same numbers as a list below.
export function NarrowPassageAnimation({ sides, scaleMean, label }: { sides: AnimationSide[]; scaleMean: number; label: string }) {
  return <><svg className="group-animation-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label}. ${sides.map(animationSideLabel).join(". ")}`} data-scale-mean={scaleMean}>
    <path className="group-animation-road" d={`M 0 ${road.top} H ${narrow.left - 20} L ${narrow.left} ${narrow.top} H ${narrow.right} L ${narrow.right + 20} ${road.top} H ${width} V ${road.bottom} H ${narrow.right + 20} L ${narrow.right} ${narrow.bottom} H ${narrow.left} L ${narrow.left - 20} ${road.bottom} H 0 Z`} />
    <text className="group-animation-caption" x={width / 2} y={narrow.bottom + 26} textAnchor="middle">Engstelle</text>
    {sides.map((side) => {
      const place = layout[side.side];
      const radius = side.mean === null || scaleMean <= 0 ? 0 : maximumRadius * Math.sqrt(Math.min(side.mean, scaleMean) / scaleMean);
      const center = (road.top + road.bottom) / 2;
      const lines = side.mean === null || side.max === null
        ? [["Keine Wartezeit", "strong"], ["erfasst", "strong"]]
        : [[`Mittel ${seconds(side.mean)}`, "strong"], [`Max. ${seconds(side.max)}`, ""], [`${formatNumber(side.count)} mit Wartezeit`, "muted"]];
      return <g key={side.side} className="group-animation-side" data-side={side.side} data-direction={side.direction} data-wait-mean={side.mean ?? ""} data-wait-max={side.max ?? ""} data-wait-count={side.count} data-radius={radius.toFixed(2)}>
        <title>{animationSideLabel(side)}</title>
        <text className="group-animation-name" x={place.textX} y={24} textAnchor={place.anchor}>Seite {side.side} · {side.name}</text>
        <text className="group-animation-caption" x={place.textX} y={42} textAnchor={place.anchor}>wartet auf die Durchfahrt nach Seite {place.toward}</text>
        {[place.outer, place.inner].map((x, index) => <g key={x}>
          <line className="group-animation-line" x1={x} y1={road.top - 8} x2={x} y2={road.bottom + 8} />
          <text className="group-animation-caption" x={x} y={road.bottom + 24} textAnchor="middle">{index === 0 ? "außen" : "innen"}</text>
        </g>)}
        <circle className="group-animation-reference" cx={place.cx} cy={center} r={maximumRadius} />
        {radius > 0 && <circle className="group-animation-wait" cx={place.cx} cy={center} r={radius} />}
        {lines.map(([text, kind], index) => <text key={text} className={`group-animation-value${kind ? ` group-animation-value-${kind}` : ""}`} x={place.textX} y={center - 12 + index * 18} textAnchor={place.anchor}>{text}</text>)}
      </g>;
    })}
  </svg>
  <ul className="group-animation-readout" aria-hidden="true">{sides.map((side) => <li key={side.side}>{animationSideLabel(side)}</li>)}</ul></>;
}
